import { parseTMDBDate } from './utils';

/**
 * Canonical rules for whether an episode has aired and can be marked watched.
 * Used by every completion path (watchlist card quick-complete button, item
 * detail dialog, media card season menus) so they can't disagree.
 *
 * TMDB data is often incomplete: upcoming episodes frequently have NO
 * air_date yet. A missing date must not be treated as "aired" — that was the
 * hole that let users complete unaired episodes. Rules:
 *
 * 1. Episode has an air_date       → available iff the date is today or past.
 * 2. Episode has no air_date, but a LATER episode in the season has aired
 *                                  → available (a data gap in aired content).
 * 3. Episode has no air_date and nothing after it has aired
 *                                  → NOT available (presumed unaired)...
 * 4. ...unless NO episode in the season has any air_date at all (very old or
 *    poorly-catalogued content) → available, so real watches aren't blocked.
 */

export interface EpisodeAirInfo {
  episode_number: number;
  air_date?: string | null;
}

function airedByNow(dateString: string | null | undefined, now: Date): boolean {
  const date = parseTMDBDate(dateString);
  return !!date && date <= now;
}

export function isEpisodeAvailable(
  episode: EpisodeAirInfo,
  seasonEpisodes: EpisodeAirInfo[],
  now: Date = new Date()
): boolean {
  if (episode.air_date) {
    return airedByNow(episode.air_date, now);
  }

  // Season has no air-date data at all — don't block the user
  if (!seasonEpisodes.some(ep => ep.air_date)) {
    return true;
  }

  // Undated episode: only available if a later episode already aired
  return seasonEpisodes.some(
    ep =>
      ep.episode_number > episode.episode_number &&
      airedByNow(ep.air_date, now)
  );
}

export function seasonHasUnairedEpisodes(
  seasonEpisodes: EpisodeAirInfo[] | undefined,
  now: Date = new Date()
): boolean {
  if (!seasonEpisodes || seasonEpisodes.length === 0) return false;
  return seasonEpisodes.some(ep => !isEpisodeAvailable(ep, seasonEpisodes, now));
}

/**
 * A season with no episode data at all renders as "0/0" — usually a
 * just-announced season TMDB hasn't populated yet. Hide it until TMDB
 * provides either episodes or a premiere air date.
 */
export function seasonHasDisplayableData(season: {
  episode_count?: number;
  air_date?: string | null;
  episodes?: EpisodeAirInfo[];
}): boolean {
  const totalEpisodes = season.episodes?.length || season.episode_count || 0;
  return totalEpisodes > 0 || !!season.air_date;
}

/** First episode of the season that hasn't aired yet (for lock messaging). */
export function firstUnairedEpisode(
  seasonEpisodes: EpisodeAirInfo[] | undefined,
  now: Date = new Date()
): EpisodeAirInfo | null {
  if (!seasonEpisodes) return null;
  return seasonEpisodes.find(ep => !isEpisodeAvailable(ep, seasonEpisodes, now)) || null;
}
